import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, Loader2, MapPin, CalendarDays, Users, Briefcase, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Avatar, ErrorState, Spinner, StatusBadge } from "@/components/common";
import { ContributionsStep, draftBody, initialDraft } from "@/components/plan/ContributionsStep";
import { InvitePanel } from "@/components/plan/InvitePanel";
import { GuestTermsList } from "@/components/plan/RequestDialog";
import { api, apiError, coverOf, formatDates, STATUS } from "@/lib/api";
import { cn } from "@/lib/utils";

const STEPS = ["The idea", "Co-hosts", "Contributions", "Guests", "Review"];

const Stepper = ({ step, setStep }) => (
  <ol className="flex items-start overflow-x-auto pb-2" aria-label="Planning steps">
    {STEPS.map((s, i) => (
      <li key={s} className="flex min-w-[96px] flex-1 items-start">
        <button type="button" onClick={() => setStep(i)} aria-current={i === step ? "step" : undefined} data-testid={`plan-step-${i}`} className="group flex flex-col items-start gap-2 text-left">
          <span className={cn("flex h-8 w-8 items-center justify-center rounded-full border text-sm transition-colors", i < step ? "border-primary bg-primary text-primary-foreground" : i === step ? "border-primary text-primary" : "border-foreground/40 text-foreground/70 group-hover:border-primary/60")}>
            {i < step ? <Check className="h-4 w-4" /> : i + 1}
          </span>
          <span className={cn("whitespace-nowrap text-sm", i === step ? "font-semibold text-primary" : "text-foreground/80")}>{s}</span>
        </button>
        {i < STEPS.length - 1 && <span className={cn("mx-3 mt-4 h-px flex-1", i < step ? "bg-primary" : "bg-foreground/25")} aria-hidden />}
      </li>
    ))}
  </ol>
);

const IdeaStep = ({ exp }) => (
  <div className="space-y-5">
    <h2 className="font-display text-4xl leading-tight sm:text-5xl">The idea</h2>
    <p className="text-xl text-foreground/90">{exp.summary}</p>
    {exp.description && <p className="whitespace-pre-line text-foreground/80">{exp.description}</p>}
    {exp.my_role === "lead" && <Link to={`/experiences/${exp.id}/edit`} className="btn-outline" data-testid="plan-edit-idea-link">Edit the idea</Link>}
  </div>
);

const CohostsStep = ({ exp, onUpdate }) => (
  <div className="space-y-6">
    <h2 className="font-display text-4xl leading-tight sm:text-5xl">The circle</h2>
    <ul className="divide-y divide-border">
      {exp.circle.map((p) => (
        <li key={p.id} className="flex items-center gap-4 py-3"><Avatar person={p} size={52} />
          <div><Link to={`/members/${p.id}`} className="font-display text-xl hover:text-primary">{p.name}</Link><p className="text-sm text-muted-foreground">{p.role === "lead" ? "Lead host" : "Co-host"}</p></div>
        </li>
      ))}
    </ul>
    {exp.my_role === "lead" && ["finding_cohosts", "proposed_terms", "agreed"].includes(exp.status) && <InvitePanel exp={exp} onUpdate={onUpdate} />}
    {exp.my_role === "lead" && exp.status === "draft" && <p className="text-sm text-muted-foreground">Publish the experience to invite co-hosts and receive requests.</p>}
  </div>
);

const GuestsStep = ({ exp, draft, setDraft }) => {
  const lead = exp.my_role === "lead" && !["closed", "cancelled"].includes(exp.status);
  const setTerm = (k) => (e) => setDraft((d) => ({ ...d, guest_terms: { ...d.guest_terms, [k]: e.target.value } }));
  return (
    <div className="space-y-6">
      <h2 className="font-display text-4xl leading-tight sm:text-5xl">Guest arrangements</h2>
      <p className="text-foreground/85">Make guest costs clear. Guests acknowledge these terms before they commit.</p>
      {lead ? (
        <div className="space-y-5" data-testid="guest-terms-editor">
          {[["covered", "Covered costs", "What the circle covers for guests"], ["excluded", "Personal extras", "What guests pay for themselves"], ["undecided", "Still undecided", "Anything not yet agreed"]].map(([k, l, ph]) => (
            <div key={k}><label htmlFor={`gt-${k}`} className="field-label">{l}</label>
              <textarea id={`gt-${k}`} rows={3} maxLength={1000} className="field py-3" placeholder={ph} value={draft.guest_terms[k]} onChange={setTerm(k)} data-testid={`guest-terms-${k}-input`} /></div>
          ))}
          <div><label htmlFor="gt-places" className="field-label">Guest places</label>
            <input id="gt-places" type="number" min={exp.guest_count} max={50} className="field max-w-[160px]" value={draft.guest_places} onChange={(e) => setDraft((d) => ({ ...d, guest_places: e.target.value }))} data-testid="guest-places-input" /></div>
        </div>
      ) : <div className="surface p-6"><GuestTermsList terms={exp.guest_terms} /></div>}
      <div>
        <h3 className="mb-2 font-semibold">Guests ({exp.guest_count}/{exp.guest_places})</h3>
        {exp.guests.length ? <ul className="flex flex-wrap gap-3">{exp.guests.map((g) => <li key={g.id} className="flex items-center gap-2 rounded-full border border-border py-1 pl-1 pr-4"><Avatar person={g} size={32} />{g.name}</li>)}</ul> : <p className="text-sm text-muted-foreground">No guests yet.</p>}
      </div>
    </div>
  );
};

const ReviewStep = ({ exp }) => (
  <div className="space-y-6">
    <h2 className="font-display text-4xl leading-tight sm:text-5xl">Review</h2>
    <dl className="grid gap-4 sm:grid-cols-3">
      {[["Status", STATUS[exp.status]?.label], ["Terms version", exp.plan.plan_version ? `v${exp.plan.plan_version}${exp.plan.plan_state === "sent" ? " · sent" : " · drafting"}` : "Not sent yet"], ["Agreed version", exp.plan.agreed_version ? `v${exp.plan.agreed_version}` : "—"]].map(([k, v]) => (
        <div key={k} className="surface p-4"><dt className="text-xs text-muted-foreground">{k}</dt><dd className="mt-1 font-display text-2xl" data-testid={`review-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</dd></div>
      ))}
    </dl>
    <div>
      <h3 className="mb-3 font-semibold">History</h3>
      <ol className="space-y-3 border-l border-border pl-5" data-testid="change-log">
        {exp.plan.change_log.map((c, i) => (
          <li key={i} className="relative text-sm"><span className="absolute -left-[25px] top-1.5 h-2 w-2 rounded-full bg-primary" aria-hidden />
            <p>{c.text}</p><p className="text-xs text-muted-foreground">{c.by} · {new Date(c.at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</p></li>
        ))}
      </ol>
    </div>
  </div>
);

const Sidebar = ({ exp, setStep }) => (
  <aside className="space-y-5 lg:sticky lg:top-24">
    <div className="surface overflow-hidden p-4">
      <img src={coverOf(exp)} alt="" className="aspect-[16/10] w-full rounded-lg object-cover" />
      <h2 className="font-display mt-4 text-3xl leading-tight">{exp.title}</h2>
      <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm"><span className="flex items-center gap-2"><MapPin className="h-4 w-4" />{exp.destination}</span><span className="flex items-center gap-2"><CalendarDays className="h-4 w-4" />{formatDates(exp)}</span></p>
      <div className="mt-3"><StatusBadge status={exp.status} /></div>
    </div>
    <div className="surface p-5">
      <h2 className="font-display text-2xl">Guest arrangements</h2>
      {[[Users, "Covered costs", "Set what the circle covers"], [Briefcase, "Personal extras", "Make guest costs clear"]].map(([I, t, s]) => (
        <button key={t} type="button" onClick={() => setStep(3)} className="flex w-full items-center gap-4 border-t border-border py-4 text-left first-of-type:mt-3 hover:text-primary" data-testid={`sidebar-${t.toLowerCase().replace(/ /g, "-")}`}>
          <I className="h-5 w-5" strokeWidth={1.5} /><span className="flex-1"><span className="block">{t}</span><span className="text-xs text-muted-foreground">{s}</span></span><ChevronRight className="h-4 w-4" />
        </button>
      ))}
      <p className="mt-2 text-xs text-muted-foreground">Planning only. Bookings and payments are arranged outside the platform.</p>
    </div>
  </aside>
);

export default function Plan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exp, setExp] = useState(null);
  const [draft, setDraft] = useState(null);
  const [step, setStep] = useState(2);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const apply = (data) => { setExp(data); setDraft(initialDraft(data)); };
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/experiences/${id}`);
      if (!data.plan || !["lead", "cohost"].includes(data.my_role)) return navigate(`/experiences/${id}`, { replace: true });
      apply(data);
    } catch (e) { setError(apiError(e)); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy("save");
    try { const { data } = await api.put(`/experiences/${id}/plan`, draftBody(draft)); apply(data); toast.success("Plan saved"); return true; }
    catch (e) { toast.error(apiError(e)); return false; } finally { setBusy(""); }
  };
  const send = async () => {
    if (!(await save())) return;
    setBusy("send");
    try { const { data } = await api.post(`/experiences/${id}/plan/send`); apply(data); toast.success("Sent to every co-host for agreement"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (error) return <div className="shell py-16"><ErrorState message={error} onRetry={load} /></div>;
  if (!exp) return <Spinner label="Opening planning space" />;
  const lead = exp.my_role === "lead" && !["closed", "cancelled"].includes(exp.status);
  const editableStep = step === 2 || step === 3;

  return (
    <div data-testid="plan-page">
      <section className="relative overflow-hidden border-b border-border">
        <img src="/brand/angels-v2.webp" alt="" aria-hidden className="absolute right-0 top-0 hidden h-full w-[55%] object-cover object-[center_15%] md:block" />
        <div className="absolute inset-y-0 right-0 hidden w-[55%] bg-gradient-to-r from-background via-background/30 to-transparent md:block" aria-hidden />
        <div className="shell relative py-10 md:py-14">
          <p className="eyebrow mb-4">Private planning space</p>
          <h1 className="font-display text-5xl leading-[0.95] lg:text-7xl">Build the experience.<br />Together.</h1>
          <p className="mt-4 text-xl sm:text-2xl">{exp.title} · {exp.plan.plan_state === "sent" ? `Terms v${exp.plan.plan_version}` : "Draft plan"}</p>
        </div>
      </section>
      <div className="shell pb-24 pt-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="min-w-0 flex-1"><Stepper step={step} setStep={setStep} /></div>
          {lead && editableStep && <button type="button" className="btn-outline shrink-0" onClick={save} disabled={!!busy} data-testid="save-plan-button">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}</button>}
        </div>
        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_400px]">
          <div className="surface min-w-0 p-5 sm:p-8" data-testid={`plan-step-content-${step}`}>
            {step === 0 && <IdeaStep exp={exp} />}
            {step === 1 && <CohostsStep exp={exp} onUpdate={apply} />}
            {step === 2 && <ContributionsStep exp={exp} draft={draft} setDraft={setDraft} onUpdate={apply} />}
            {step === 3 && <GuestsStep exp={exp} draft={draft} setDraft={setDraft} />}
            {step === 4 && <ReviewStep exp={exp} />}
            {lead && editableStep && exp.plan.plan_state !== "sent" && (
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button type="button" className="btn-primary min-h-[52px] sm:min-w-[260px]" onClick={send} disabled={!!busy} data-testid="send-for-agreement-button">{busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send for agreement"}</button>
                <p className="text-sm text-muted-foreground">Requires 100% allocated and at least one co-host.</p>
              </div>
            )}
            <div className="mt-8 flex justify-between border-t border-border pt-6">
              <button type="button" className="btn-outline min-h-[44px]" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="plan-prev-button">Previous</button>
              <button type="button" className="btn-outline min-h-[44px]" disabled={step === 4} onClick={() => setStep(step + 1)} data-testid="plan-next-button">Next</button>
            </div>
          </div>
          <Sidebar exp={exp} setStep={setStep} />
        </div>
        <div className="mt-6 flex flex-wrap gap-4">
          <Link to={`/experiences/${exp.id}`} className="btn-link" data-testid="plan-back-to-experience">Back to experience</Link>
          {exp.thread_id && <Link to={`/messages/${exp.thread_id}`} className="btn-link" data-testid="plan-open-chat">Circle conversation</Link>}
        </div>
      </div>
    </div>
  );
}
